import React from "react";
import { Dialog, DialogPanel, DialogTitle } from "@headlessui/react";
import { CheckCircle2, Loader2, Save, X } from "lucide-react";

import StatusPill from "../../../../components/common/StatusPill";
import { Switch } from "../../../../components/ui/switch";

export default function RoleDrawer({
  open,
  mode,
  value,
  onChange,
  onClose,
  onSave,
  saving,
  canManage,
}) {
  const isEdit = mode === "edit";
  const topOffset = "var(--app-shell-topbar-size, 4rem)";

  if (!open) return null;

  const set = (key, nextValue) =>
    onChange(prev => ({ ...(prev || {}), [key]: nextValue }));

  return (
    <Dialog open={open} onClose={() => { if (!saving) onClose(); }} className="fixed inset-x-0 bottom-0 z-[60]" style={{ top: topOffset }}>
      <div
        className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm"
        onClick={saving ? undefined : onClose}
      />

      <DialogPanel className="absolute right-0 top-0 h-full w-full border-l border-slate-200 bg-white sm:w-[480px]">
        <div className="flex h-full flex-col">
          <div className="relative overflow-hidden border-b border-slate-200 px-5 py-4">
            <div className="absolute inset-0 bg-slate-50" />
            <div className="relative flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-400">
                  {isEdit ? "Role editor" : "Role creation"}
                </p>
                <DialogTitle className="mt-1 text-base font-semibold text-slate-900">
                  {isEdit ? "Edit role" : "Create a role"}
                </DialogTitle>
                <p className="mt-0.5 text-[13px] text-slate-500">
                  {isEdit
                    ? "Update role name, status, and description."
                    : "Create role first, then map permissions."}
                </p>
              </div>

              <div className="flex items-center gap-3">
                <StatusPill isActive={!!value?.isActive} />
                <button
                  type="button"
                  onClick={saving ? undefined : onClose}
                  className="rounded-lg border border-slate-200 bg-white p-2 text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
                  disabled={saving}
                  title="Close"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div className="flex-1 overflow-auto bg-white px-5 py-5 sm:px-6">
            <div className="mx-auto max-w-lg space-y-6">
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Role Name</h3>
                <p className="mt-0.5 text-[13px] text-slate-500">
                  Set a clear name for the role.
                </p>
                <div className="mt-4">
                  <input
                    aria-label="Role name"
                    disabled={saving || !canManage}
                    value={value?.name ?? ""}
                    onChange={e => set("name", e.target.value)}
                    placeholder="e.g., Support"
                    className="w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-500/10"
                  />
                </div>
              </div>

              <hr className="border-slate-100" />
              <div>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">Status</h3>
                    <p className="mt-0.5 text-[13px] text-slate-500">
                      Inactive roles remain visible but cannot be assigned.
                    </p>
                  </div>
                  <div className="flex items-center gap-3 mt-1">
                    <span className="text-sm font-medium text-slate-700">
                      {value?.isActive ? "Active" : "Inactive"}
                    </span>
                    <Switch
                      checked={!!value?.isActive}
                      onCheckedChange={checked => set("isActive", checked)}
                      disabled={!canManage || saving}
                      className="data-[state=checked]:bg-slate-900 data-[state=unchecked]:bg-slate-300 focus-visible:ring-slate-400 border-none"
                    />
                  </div>
                </div>
              </div>

              <hr className="border-slate-100" />
              <div>
                <h3 className="text-sm font-semibold text-slate-900">Description</h3>
                <p className="mt-0.5 text-[13px] text-slate-500">
                  Provide context about what this role grants access to.
                </p>
                <textarea
                  rows={5}
                  aria-label="Role description"
                    disabled={saving || !canManage}
                    value={value?.description ?? ""}
                  onChange={e => set("description", e.target.value)}
                  placeholder="Describe what this role should manage, approve, or access."
                  className="mt-4 w-full rounded-lg border border-slate-200 bg-slate-50/50 px-3.5 py-2 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-500 focus:bg-white focus:ring-4 focus:ring-sky-500/10"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200 bg-white px-5 py-3">
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={saving ? undefined : onClose}
                disabled={saving}
                className="rounded-lg border border-slate-200 px-3.5 py-1.5 text-[13px] font-medium text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:opacity-60"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={onSave}
                disabled={!canManage || saving}
                className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3.5 py-1.5 text-[13px] font-medium text-white transition hover:bg-slate-800 disabled:opacity-60"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isEdit ? (
                  <Save className="h-4 w-4" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                {saving ? "Saving..." : isEdit ? "Save changes" : "Create role"}
              </button>
            </div>
          </div>
        </div>
      </DialogPanel>
    </Dialog>
  );
}
